// ---------------------------------------------------------------------------
// Geometria e memória da janela do painel da Section box.
//
// A janela vive DENTRO da área do 3D (posição relativa ao contentor do
// canvas) e nunca sai dela. A posição e o estado minimizado ficam
// guardados por browser (localStorage), sempre com try/catch: num modo
// privado ou com o armazenamento bloqueado, a janela abre na posição por
// omissão e o resto funciona.
//
// Por omissão abre do lado direito, por BAIXO do cubo de navegação (que
// ocupa o canto superior direito). Se a área não tiver altura para isso,
// abre à esquerda do cubo, no topo. Antes abria no canto inferior
// direito e, em ecrãs baixos, subia por cima do cubo.
//
// Puro (menos as duas funções de armazenamento, que recebem o Storage).
// ---------------------------------------------------------------------------

export interface Ponto {
  x: number
  y: number
}

export interface Tamanho {
  w: number
  h: number
}

export interface EstadoJanela {
  /** Posição relativa ao contentor do 3D; null = por omissão. */
  pos: Ponto | null
  minimizado: boolean
}

export const CHAVE_JANELA = 'vn2r.sectionBox.janela'

/** Cubo de navegação: `right-3 top-3`, 160 px (ver view-cube). */
export const CUBO = { tamanho: 160, margem: 12 }

/** Folga entre a janela e o que está à volta. */
export const FOLGA = 8

/** Mantém a janela inteira dentro da área. Numa área mais pequena do
 *  que a janela, encosta-a ao canto superior esquerdo. */
export function limitarPosicao(p: Ponto, janela: Tamanho, area: Tamanho): Ponto {
  const maxX = Math.max(0, area.w - janela.w)
  const maxY = Math.max(0, area.h - janela.h)
  return {
    x: Math.round(Math.min(Math.max(0, p.x), maxX)),
    y: Math.round(Math.min(Math.max(0, p.y), maxY)),
  }
}

/** Posição por omissão: à direita, por baixo do cubo; sem altura para
 *  isso, à esquerda do cubo, no topo. Sempre dentro da área. */
export function posicaoPorOmissao(
  area: Tamanho,
  janela: Tamanho,
  cubo: { tamanho: number; margem: number } = CUBO,
): Ponto {
  const x = area.w - janela.w - cubo.margem
  const yAbaixo = cubo.margem + cubo.tamanho + FOLGA
  if (yAbaixo + janela.h + cubo.margem <= area.h) {
    return limitarPosicao({ x, y: yAbaixo }, janela, area)
  }
  return limitarPosicao(
    {
      x: area.w - cubo.margem - cubo.tamanho - FOLGA - janela.w,
      y: cubo.margem,
    },
    janela,
    area,
  )
}

/** Lê o estado guardado. Qualquer falha dá o estado por omissão. */
export function lerJanela(storage?: Storage | null): EstadoJanela {
  const vazio: EstadoJanela = { pos: null, minimizado: false }
  try {
    const s = storage ?? (typeof localStorage === 'undefined' ? null : localStorage)
    const raw = s?.getItem(CHAVE_JANELA)
    if (!raw) return vazio
    const o = JSON.parse(raw) as Partial<{ pos: Partial<Ponto> | null; minimizado: unknown }>
    const pos =
      o.pos &&
      typeof o.pos.x === 'number' &&
      Number.isFinite(o.pos.x) &&
      typeof o.pos.y === 'number' &&
      Number.isFinite(o.pos.y)
        ? { x: o.pos.x, y: o.pos.y }
        : null
    return { pos, minimizado: o.minimizado === true }
  } catch {
    return vazio
  }
}

/** Grava o estado. Falhar a gravar não é erro: perde-se só a memória. */
export function gravarJanela(estado: EstadoJanela, storage?: Storage | null): void {
  try {
    const s = storage ?? (typeof localStorage === 'undefined' ? null : localStorage)
    s?.setItem(CHAVE_JANELA, JSON.stringify(estado))
  } catch {
    /* armazenamento cheio ou bloqueado */
  }
}
